import { centsToCurrency, formatCurrency } from '@/utils/currencyHelpers'
import type { SignUpTaxPackageTerms } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'

export const formatPayrollPackageLine = (
  payroll: SignUpTaxPackageTerms['payroll']
): string | null => {
  if (!payroll?.freeMonths) return null

  const { freeMonths, percentOff, amountOffInCents, monthlyPriceInCents } =
    payroll

  if (percentOff === 100) {
    const value = formatCurrency(
      centsToCurrency(monthlyPriceInCents).multiply(freeMonths),
      { hideDecimalsIfZero: true }
    )
    return `${freeMonths} free months of payroll (${value} value)`
  }

  if (typeof amountOffInCents === 'number' && amountOffInCents > 0) {
    const value = formatCurrency(
      centsToCurrency(amountOffInCents).multiply(freeMonths),
      { hideDecimalsIfZero: true }
    )
    return `Free payroll for yourself for ${freeMonths} months (${value} value)`
  }

  if (typeof percentOff === 'number') {
    return `${percentOff}% off payroll for ${freeMonths} months`
  }

  return null
}

export const formatTaxAdvisoryPackageLine = (
  taxAdvisory: SignUpTaxPackageTerms['taxAdvisory']
): string | null => {
  if (typeof taxAdvisory?.percentOff !== 'number') return null

  if (taxAdvisory.percentOff === 100) {
    return 'Complimentary 30 min tax consult ($150 value)'
  }

  return `${taxAdvisory.percentOff}% off a 30 min tax consult ($150 value)`
}

const DEFAULT_BANKING_BONUS_COPY =
  'Open a Heard Bank account for 1-2% cash back, and get $100 when you move $3K of direct deposit within your first 60 days. Terms apply.'

/** Reads the `signup-banking-bonus` flag: `{ enabled: true, copy? }` yields the line, anything else yields nothing. */
export const resolveBankingBonusLine = (flagValue: unknown): string | null => {
  if (typeof flagValue !== 'object' || flagValue === null) return null
  if (!('enabled' in flagValue) || flagValue.enabled !== true) return null
  const copy =
    'copy' in flagValue && typeof flagValue.copy === 'string'
      ? flagValue.copy.trim()
      : ''
  return copy || DEFAULT_BANKING_BONUS_COPY
}
